import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import toast from 'react-hot-toast';
import api, { User as ApiUser } from '@/services/api';

interface Organization {
  id: string;
  name: string;
  country: string;
  isOwner: boolean;
}

interface UserOrganization {
  organizationId: string;
  roleId: string;
  role: string;
  joinedAt: string;
  isActive: boolean;
}

interface User {
  id: string;
  name: string;
  email: string;
  phone?: string;
  country?: string;
  avatar?: string;
  organizations: UserOrganization[];
  // Legacy fields for backward compatibility
  organization?: Organization;
  role: 'Owner' | 'Admin' | 'Manager' | 'Member' | 'Viewer' | 'User' | 'SuperAdmin';
  roleId?: string; // Reference to the Role document for detailed permissions
}

interface RegisterData {
  organizationName: string;
  phone: string;
  country: string;
}

/** `login()`'s result when the account has MFA enabled — password was correct, but the session isn't issued yet. */
export interface MfaRequiredResult {
  requiresMfa: true;
  challengeToken: string;
  mfaMethod: 'totp' | 'email';
  message: string;
}

interface AuthContextType {
  user: User | null;
  activeOrganization: UserOrganization | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  needsOnboarding: boolean;
  needsOrganizationSelection: boolean;
  login: (email: string, password: string, captchaToken?: string) => Promise<MfaRequiredResult | void>;
  completeMfaLogin: (challengeToken: string, code: string) => Promise<void>;
  register: (
    name: string,
    email: string,
    password: string,
    orgData?: RegisterData,
    captchaToken?: string
  ) => Promise<void>;
  googleSignIn: (credential?: string) => Promise<MfaRequiredResult | void>;
  logout: () => void;
  updateProfile: (data: Partial<User>) => Promise<void>;
  createOrganization: (name: string, country: string) => Promise<void>;
  setActiveOrganization: (org: UserOrganization | null) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [activeOrganization, setActiveOrganizationState] = useState<UserOrganization | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const setActiveOrganization = async (org: UserOrganization | null) => {
    if (!org) {
      setActiveOrganizationState(null);
      return;
    }

    try {
      const response = await api.post('/organizations/select', { 
        organizationId: org.organizationId 
      });
      
      if (response.data?.success) {
        const { data } = response.data;
        localStorage.setItem('token', data.token);
        setActiveOrganizationState(org);
        
        // Update user state with selected organization
        setUser(prev => prev ? {
          ...prev,
          organizationId: data.organizationId,
          roleId: data.roleId,
          role: data.role as User['role']
        } : null);
      }
    } catch (error) {
      console.error('Failed to select organization:', error);
      toast.error('Failed to select organization');
      throw error;
    }
  };

  const isAuthenticated = !!user;
  const needsOnboarding = isAuthenticated && (!user?.organizations || user.organizations.length === 0);
  const needsOrganizationSelection = isAuthenticated && 
    user?.organizations && 
    user.organizations.length > 0 && 
    !activeOrganization;

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          // Validate token and load current user profile from backend
          const response = await api.get<{ success: boolean; data: ApiUser }>('/users/profile');

          if (response.data?.success && response.data.data) {
            const apiUser = response.data.data;
            const userData: User = {
              id: apiUser._id,
              name: apiUser.name,
              email: apiUser.email,
              role: apiUser.role as User['role'],
              roleId: apiUser.roleId,
              organizations: apiUser.organizations || [],
              // Legacy compatibility
              organization: apiUser.organizationId
                ? {
                    id: apiUser.organizationId,
                    name: '',
                    country: '',
                    isOwner: apiUser.role === 'Owner',
                  }
                : undefined,
            };
            
            setUser(userData);
            
            // Restore the active organization from the user's profile.
            // The backend stores the active org in organizationId and isActive, so we
            // restore it here instead of forcing the user to select again on every refresh.
            const savedActiveOrg = userData.organizations.find(
              org => org.organizationId === apiUser.organizationId
            ) || (userData.organizations.length === 1 ? userData.organizations[0] : null);
            
            if (savedActiveOrg && !activeOrganization) {
              setActiveOrganizationState(savedActiveOrg);
            }
          } else {
            localStorage.removeItem('token');
            setUser(null);
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('token');
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Shared by login() and completeMfaLogin() — both end up with the same
  // `{ token, user }` shape from the backend (see authService.completeLogin),
  // so the "store token + build User state + auto-select org" logic lives
  // in one place instead of being duplicated across both entry points.
  const applyLoginResult = async (data: { token: string; user?: ApiUser }, fallbackEmail: string) => {
    if (!data?.token) {
      throw new Error('Login failed: missing token');
    }

    localStorage.setItem('token', data.token);

    const apiUser = data.user;
    const userData = apiUser
      ? {
          id: apiUser._id,
          name: apiUser.name,
          email: apiUser.email,
          role: apiUser.role as User['role'],
          roleId: apiUser.roleId,
          organizations: apiUser.organizations || [],
          organization: apiUser.organizationId
            ? {
                id: apiUser.organizationId,
                name: '',
                country: '',
                isOwner: apiUser.role === 'Owner',
              }
            : undefined,
        }
      : {
          id: '1',
          name: 'User',
          email: fallbackEmail,
          role: 'User' as User['role'],
          organizations: [],
        };

    setUser(userData);

    if (userData.organizations && userData.organizations.length === 1) {
      await setActiveOrganization(userData.organizations[0]);
    }
  };

  const login = async (email: string, password: string, captchaToken?: string): Promise<MfaRequiredResult | void> => {
    try {
      setIsLoading(true);
      const response = await api.post('/auth/login', { email, password, captchaToken });
      const body = response.data as any;

      if (body?.requiresMfa) {
        return {
          requiresMfa: true,
          challengeToken: body.data.challengeToken,
          mfaMethod: body.data.mfaMethod,
          message: body.message,
        };
      }

      await applyLoginResult(body.data, email);
      toast.success('Login successful!');
    } catch (error: any) {
      console.error('Login error:', error);
      // Let caller handle EMAIL_NOT_VERIFIED; show generic toast for all other failures
      if (error?.response?.data?.code !== 'EMAIL_NOT_VERIFIED') {
        toast.error(error?.response?.data?.message || 'Login failed. Please check your credentials.');
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const completeMfaLogin = async (challengeToken: string, code: string) => {
    try {
      setIsLoading(true);
      const response = await api.post('/auth/mfa/verify-login', { challengeToken, code });
      const { data } = response.data as any;
      await applyLoginResult(data, '');
      toast.success('Login successful!');
    } catch (error: any) {
      console.error('MFA verification error:', error);
      toast.error(error?.response?.data?.message || 'Incorrect or expired code.');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (
    name: string,
    email: string,
    password: string,
    orgData?: RegisterData,
    captchaToken?: string
  ) => {
    try {
      setIsLoading(true);
      const response = await api.post('/auth/register', {
        name,
        email,
        password,
        phone: orgData?.phone,
        country: orgData?.country,
        organizationName: orgData?.organizationName,
        captchaToken,
      });

      // Backend now returns { email } only (no token) until email is verified
      toast.success('Account created! Please check your email to verify your account.');
    } catch (error) {
      console.error('Registration error:', error);
      const msg = (error as any)?.response?.data?.message || 'Registration failed. Please try again.';
      toast.error(msg);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const googleSignIn = async (credential?: string): Promise<MfaRequiredResult | void> => {
    try {
      setIsLoading(true);
      
      if (!credential) {
        // This should not happen as the component handles the Google flow
        throw new Error('Google credential is required');
      }
      
      const response = await api.post('/auth/google/google-one-tap', {
        credential
      });

      if (response.data?.requiresMfa) {
        return {
          requiresMfa: true,
          challengeToken: response.data.data.challengeToken,
          mfaMethod: response.data.data.mfaMethod,
          message: response.data.message,
        };
      }

      if (response.data?.success && response.data.data) {
        const { token, user: userData } = response.data.data;
        
        // Store token
        localStorage.setItem('token', token);
        
        // Build user state
        const newUser = {
          id: userData.id,
          name: userData.name,
          email: userData.email,
          phone: userData.phone,
          country: userData.country,
          avatar: userData.avatar,
          role: userData.role as User['role'],
          roleId: userData.roleId,
          organizations: userData.organizations || [],
          organization: userData.organizationId
            ? {
                id: userData.organizationId,
                name: '',
                country: '',
                isOwner: userData.role === 'Owner',
              }
            : undefined,
        };

        // Update user state
        setUser(newUser);

        // Auto-select single organization
        if (newUser.organizations && newUser.organizations.length === 1) {
          await setActiveOrganization(newUser.organizations[0]);
        }
        
        toast.success('Signed in with Google successfully!');
      }
    } catch (error) {
      console.error('Google Sign-In error:', error);
      const msg = (error as any)?.response?.data?.message || 'Google Sign-In failed';
      toast.error(msg);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    toast.success('Logged out successfully');
  };

  const updateProfile = async (data: Partial<User>) => {
    try {
      setIsLoading(true);
      await api.put('/users/profile', data);

      setUser(prev => prev ? { ...prev, ...data } : null);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error('Failed to update profile');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const createOrganization = async (name: string, country: string) => {
    try {
      setIsLoading(true);
      const response = await api.post('/organizations', { name, country });
      const { data } = response.data as any;

      localStorage.setItem('token', data.token);

      const newOrg = {
        organizationId: data.organization.id,
        roleId: data.roleId || '',
        role: data.role || 'Owner',
        joinedAt: new Date().toISOString(),
        isActive: true,
      };

      setUser(prev => prev ? {
        ...prev,
        role: 'Owner' as const,
        roleId: data.roleId || prev.roleId,
        organizations: [...(prev.organizations || []), newOrg],
        organization: {
          id: data.organization.id,
          name: data.organization.name,
          country: data.organization.country,
          isOwner: true,
        },
      } : null);

      await setActiveOrganization(newOrg);

      toast.success(response.data.message || 'Organization ready!');
    } catch (error: any) {
      console.error('Create organization error:', error);
      const msg = error?.response?.data?.message || 'Failed to create organization. Please try again.';
      toast.error(msg);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    activeOrganization,
    isLoading,
    isAuthenticated,
    needsOnboarding,
    needsOrganizationSelection,
    login,
    completeMfaLogin,
    register,
    googleSignIn,
    logout,
    updateProfile,
    createOrganization,
    setActiveOrganization,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
